import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
} from '@safetyhub/ui';
import {
  AGENCY_OBLIGATION_OVERLAY_KIND_FIELDS,
  AGENCY_OBLIGATION_OVERLAY_KIND_LABELS,
  AGENCY_OBLIGATION_OVERLAY_KINDS,
  agencyObligationOverlayFieldLabel,
  agencyObligationOverlayFormBlockedReason,
  agencyObligationOverlayToForm,
  buildCreateAgencyObligationOverlayBody,
  buildUpdateAgencyObligationOverlayBody,
  deriveAgencyObligationOverlayKey,
  emptyAgencyObligationOverlayForm,
  formatDateToLocal,
  type AgencyObligationOverlayCatalogOption,
  type AgencyObligationOverlayFormState,
  type AgencyObligationOverlayKindValue,
  type AgencyObligationOverlayView,
} from '@safetyhub/shared';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { apiErrorMessage } from '../../lib/api';
import {
  createAgencyOverlay,
  getAgencyOverlayHistory,
  getAgencyOverlayWorkspace,
  legalObligationOverlayQueryKeys,
  updateAgencyOverlay,
} from '../../lib/legalObligationOverlays';
import { usePermissions } from '../../hooks/usePermissions';
import { EditModalFooter } from '../../components/common/EditModalFooter';
import { ChevronLeftIcon } from '../../components/icons';

/**
 * **우리 기관 서식** — 대행 관리하는 현장의 법정 서류 목록에 「우리 기관은 공통 서식을 이렇게 쓴다」를 넣는 화면
 * (사용자 결정 2026-10-30: 저작 주체 = admin · 기관 웹 둘 다 — 이 화면은 **자기 조직** 것만).
 *
 * 열림 조건은 서버가 정한다(`AgencyObligationOverlayAuthoringService` docblock): 조회 `obligation.view` ·
 * 쓰기 `obligation.manage` · 그 조직이 **대행 관리하는 현장(수검업체가 우리와 다른 현장)이 하나 이상** · 피처 플래그.
 * 대행 현장이 없으면 서버가 `isAgency: false`를 주고 이 화면은 안내만 보인다.
 */
export default function AgencyOverlaysPage() {
  const companyId = useAuthStore((s) => s.currentMembership?.companyId);
  const { can } = usePermissions();
  const canManage = can('obligation.manage');
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [editing, setEditing] = useState<AgencyObligationOverlayView | 'new' | null>(null);
  const [historyOf, setHistoryOf] = useState<AgencyObligationOverlayView | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: legalObligationOverlayQueryKeys.workspace(companyId ?? ''),
    queryFn: () => getAgencyOverlayWorkspace(companyId!),
    enabled: !!companyId,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: legalObligationOverlayQueryKeys.all });
    // 의무 목록이 변형을 얹으므로 함께 무효화한다
    void queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
  };

  const toggle = useMutation({
    mutationFn: (row: AgencyObligationOverlayView) =>
      updateAgencyOverlay(companyId!, row.id, { isActive: !row.isActive }),
    onSuccess: (_r, row) => {
      showToast(row.isActive ? '사용 중지했습니다.' : '다시 사용합니다.', 'success');
      invalidate();
    },
    onError: (e) => showToast(apiErrorMessage(e) ?? '저장하지 못했습니다.', 'error'),
  });

  const overlays = data?.overlays ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-0">
      <Link
        to="/legal-documents"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-ink-500 hover:text-ink-900"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        현장 목록
      </Link>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">우리 기관 서식</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            공통 서식에서 빼거나, 이름을 바꾸거나, 나누거나, 더해 쓰는 항목입니다. 우리 기관이 대행 관리하는
            현장의 법정 서류 목록에 적용됩니다.
          </p>
        </div>
        {data?.isAgency && canManage && (
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            변형 추가
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">{apiErrorMessage(error) ?? '불러오지 못했습니다.'}</p>
        </div>
      ) : !data?.isAgency ? (
        <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
          <p className="text-sm font-medium text-ink-900">대행 관리하는 현장이 없습니다</p>
          <p className="mt-1 text-sm text-ink-500">
            다른 업체의 현장을 대행 관리하는 조직만 서식 변형을 둘 수 있습니다.
          </p>
        </Card>
      ) : overlays.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
          <p className="text-sm font-medium text-ink-900">공통 서식을 그대로 쓰고 있습니다</p>
          <p className="mt-1 text-sm text-ink-500">대행 현장 {data.agencySiteCount}곳에 공통 서식이 적용됩니다.</p>
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <ul className="divide-y divide-ink-200" aria-label="서식 변형 목록">
            {overlays.map((row) => (
              <li key={row.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6 ${row.isActive ? '' : 'bg-surface-soft'}`}>
                <Badge tone={row.isActive ? 'brand' : 'slate'} size="sm">
                  {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[row.kind]}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-ink-900">
                    {row.title ?? row.obligation?.title ?? row.overlayKey}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-ink-500">
                    {row.obligation ? `공통 서식: ${row.obligation.title}` : '공통 서식에 없는 항목'}
                    {!row.isActive && ' · 사용 중지'}
                  </p>
                </div>
                <div className="flex flex-none gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setHistoryOf(row)}>
                    이력
                  </Button>
                  {canManage && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggle.mutate(row)}
                        disabled={toggle.isPending && toggle.variables?.id === row.id}
                      >
                        {row.isActive ? '사용 중지' : '다시 사용'}
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => setEditing(row)}>
                        편집
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {editing && companyId && data && (
        <OverlayFormModal
          key={editing === 'new' ? 'new' : editing.id}
          row={editing === 'new' ? null : editing}
          catalog={data.catalog}
          onClose={() => setEditing(null)}
          save={(form, row) =>
            row
              ? updateAgencyOverlay(companyId, row.id, buildUpdateAgencyObligationOverlayBody(row, form) ?? {})
              : createAgencyOverlay(companyId, buildCreateAgencyObligationOverlayBody(form))
          }
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
      {historyOf && companyId && (
        <OverlayHistoryModal companyId={companyId} row={historyOf} onClose={() => setHistoryOf(null)} />
      )}
    </div>
  );
}

function OverlayFormModal({
  row,
  catalog,
  onClose,
  save,
  onSaved,
}: {
  row: AgencyObligationOverlayView | null;
  catalog: AgencyObligationOverlayCatalogOption[];
  onClose: () => void;
  save: (form: AgencyObligationOverlayFormState, row: AgencyObligationOverlayView | null) => Promise<unknown>;
  onSaved: () => void;
}) {
  const { showToast } = useToastStore();
  const mode = row ? 'edit' : 'create';
  const initial = useMemo(() => (row ? agencyObligationOverlayToForm(row) : emptyAgencyObligationOverlayForm()), [row]);
  const [form, setForm] = useState<AgencyObligationOverlayFormState>(initial);
  const fields = AGENCY_OBLIGATION_OVERLAY_KIND_FIELDS[form.kind];
  const unchanged = row !== null && buildUpdateAgencyObligationOverlayBody(row, form) === null;
  const blockedReason =
    agencyObligationOverlayFormBlockedReason(form, mode) ?? (unchanged ? '바뀐 칸이 없습니다.' : null);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);
  const obligationKey = row?.obligation?.key ?? catalog.find((c) => c.id === form.obligationId)?.key ?? null;
  const keyPreview = row
    ? row.overlayKey
    : deriveAgencyObligationOverlayKey({ kind: form.kind, obligationKey, partKey: form.partKey, additionKey: form.additionKey });

  const mutation = useMutation({
    mutationFn: () => save(form, row),
    onSuccess: () => {
      showToast(row ? '저장했습니다.' : '추가했습니다.', 'success');
      onSaved();
    },
    // 모달을 닫지 않는다 — 409(같은 항목 · 동시 수정)여도 입력을 잃지 않게
    onError: (e) => showToast(apiErrorMessage(e) ?? '저장하지 못했습니다.', 'error'),
  });

  const set = <K extends keyof AgencyObligationOverlayFormState>(key: K, value: AgencyObligationOverlayFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={row ? '서식 변형 편집' : '서식 변형 추가'}
      size="md"
      closeLocked={mutation.isPending}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason ?? ''}
          submitLabel="저장"
          submitDisabled={blockedReason !== null || mutation.isPending}
          busy={mutation.isPending}
          busyLabel="저장 중..."
          onSubmit={() => mutation.mutate()}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className={FORM_LABEL_CLASS}>종류</span>
          {row ? (
            <p className="text-sm text-ink-900">
              {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[row.kind]}
              <span className="ml-1 text-xs text-ink-500">(종류·대상은 바꿀 수 없습니다 — 사용 중지하고 새로 추가하세요)</span>
            </p>
          ) : (
            <select
              value={form.kind}
              onChange={(e) => set('kind', e.target.value as AgencyObligationOverlayKindValue)}
              className={FORM_CONTROL_CLASS}
            >
              {AGENCY_OBLIGATION_OVERLAY_KINDS.map((k) => (
                <option key={k} value={k}>
                  {AGENCY_OBLIGATION_OVERLAY_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          )}
        </label>

        {fields.needsObligation && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>공통 서식 항목</span>
            {row ? (
              <p className="text-sm text-ink-900">{row.obligation?.title ?? '—'}</p>
            ) : (
              <select value={form.obligationId} onChange={(e) => set('obligationId', e.target.value)} className={FORM_CONTROL_CLASS}>
                <option value="">항목을 고르세요</option>
                {catalog.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}

        {!row && fields.needsPartKey && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>나눈 항목 키</span>
            <input type="text" value={form.partKey} maxLength={64} onChange={(e) => set('partKey', e.target.value)} className={FORM_CONTROL_CLASS} />
            <span className="mt-1 block text-xs text-ink-500">영문 소문자·숫자·밑줄·하이픈 (예: special)</span>
          </label>
        )}
        {!row && fields.needsAdditionKey && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>항목 키</span>
            <input type="text" value={form.additionKey} maxLength={64} onChange={(e) => set('additionKey', e.target.value)} className={FORM_CONTROL_CLASS} />
            <span className="mt-1 block text-xs text-ink-500">영문 소문자·숫자·밑줄·하이픈 (예: manager-interview)</span>
          </label>
        )}
        <p className="text-xs text-ink-500">
          식별자 <code>{keyPreview || '—'}</code>
        </p>

        {fields.usesTitle ? (
          <>
            <label className="block">
              <span className={FORM_LABEL_CLASS}>우리 기관이 쓰는 이름</span>
              <input type="text" value={form.title} maxLength={300} onChange={(e) => set('title', e.target.value)} className={FORM_CONTROL_CLASS} />
            </label>
            <label className="block">
              <span className={FORM_LABEL_CLASS}>설명</span>
              <textarea value={form.description} maxLength={5000} onChange={(e) => set('description', e.target.value)} className={FORM_TEXTAREA_CLASS} />
            </label>
          </>
        ) : (
          <p className="text-sm text-ink-600">
            「쓰지 않음」은 우리 기관 서식에서 이 항목을 빼고, 대행 현장에 이행 기록을 만들지 않습니다.
          </p>
        )}

        <label className="block">
          <span className={FORM_LABEL_CLASS}>표시 순서</span>
          <input type="number" min={0} value={form.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} className={FORM_CONTROL_CLASS} />
        </label>
        <label className="block">
          <span className={FORM_LABEL_CLASS}>메모</span>
          <textarea value={form.sourceNote} maxLength={2000} onChange={(e) => set('sourceNote', e.target.value)} className={FORM_TEXTAREA_CLASS} />
        </label>
        <label className="block">
          <span className={FORM_LABEL_CLASS}>변경 사유</span>
          <input type="text" value={form.changeReason} maxLength={2000} onChange={(e) => set('changeReason', e.target.value)} className={FORM_CONTROL_CLASS} />
        </label>
      </div>
    </Modal>
  );
}

function OverlayHistoryModal({
  companyId,
  row,
  onClose,
}: {
  companyId: string;
  row: AgencyObligationOverlayView;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: legalObligationOverlayQueryKeys.history(companyId, row.id),
    queryFn: () => getAgencyOverlayHistory(companyId, row.id),
  });
  const items = data ?? [];
  return (
    <Modal open onClose={onClose} title="변경 이력" size="md">
      {isLoading ? (
        <p className="py-6 text-center text-sm text-ink-500">불러오는 중...</p>
      ) : error ? (
        <p role="alert" className="text-sm text-rose-700">{apiErrorMessage(error) ?? '이력을 불러오지 못했습니다.'}</p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-500">이력이 없습니다.</p>
      ) : (
        <ol className="space-y-3">
          {items.map((h) => (
            <li key={h.id} className="rounded-card border border-ink-200 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-ink-900">
                  {h.action === 'CREATED' ? '추가' : '변경'}
                  {h.changedByKind && (
                    <span className="ml-2 text-xs font-normal text-ink-500">
                      {h.changedByKind === 'ADMIN' ? '운영사' : '우리 기관'}
                    </span>
                  )}
                </span>
                <span className="text-xs text-ink-500">{formatDateToLocal(new Date(h.changedAt))}</span>
              </div>
              {h.changedFields.length > 0 && (
                <p className="mt-1 text-ink-700">바뀐 칸: {h.changedFields.map(agencyObligationOverlayFieldLabel).join(', ')}</p>
              )}
              {h.changeReason && <p className="mt-1 text-ink-600">사유: {h.changeReason}</p>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
